import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  ContinueWithoutAccountButton,
  CreateAccountButton,
  SignInButton,
} from "#slide-footer-buttons";
import { SlideFooter } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._components.footer";
import { SlideHanziScriptTableSlot } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._components.mdx.slide-hanzi-script-table";
import { SlideHanziScriptToggle } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._components.mdx.slide-hanzi-script-toggle";
import { SlidePageBlock } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._components.mdx.slide-page-block";
import { SlideTitle } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._components.mdx.slide-title";
import {
  Caption,
  SlideGlyph,
  SlideToneSwatch,
  Table,
  TBody,
  TD,
  TFoot,
  TH,
  THead,
  TR,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._components.mdx.slide-table";
import { SlideOptions } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._components.mdx.slide-options";
import { SlideToneColorsToggle } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._components.mdx.slide-tone-colors-toggle";
import { SlideTextSizeSlider } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._components.mdx.slide-text-size-slider";
import { SlideSvgGlyph } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._components.mdx.slide-svg-glyph";
import type { ComponentProps } from "react";
import { SlideMdxWrapper } from "#slide-mdx-wrapper";

type TextProps = ComponentProps<typeof Text>;

// Custom components for grapheme-file `:::slide` bodies (see `assets/slides`), shared with `:::quiz`
// bodies (the quiz card adds `Quiz` and `QuizOption`).
// `wrapper` hosts the whole document — see {@link SlideMdxWrapper}.
//
// Block-level prose is `selectable`: holding it opens the system text selection (a Copy menu for
// the paragraph on iOS). The deck's swipe needs 4pt of horizontal travel, so a stationary hold
// never starts it, and once the native long press is recognized the swipe fails for the rest of
// that touch. Inline runs (`em`/`strong`/`Span`) follow the paragraph that contains them.
export const slideMdxComponents = {
  blockquote: (props: TextProps) => (
    <Text
      className="mt-0 text-left leading-[1.35] sm:mt-0"
      selectable
      variant="blockquote"
      {...props}
    />
  ),
  code: (props: TextProps) => <Text selectable variant="code" {...props} />,
  ContinueWithoutAccountButton,
  CreateAccountButton,
  em: (props: TextProps) => <Text className="italic" {...props} />,
  h2: (props: TextProps) => <Text className="text-left" selectable variant="h2" {...props} />,
  h3: (props: TextProps) => <Text className="text-left" selectable variant="h3" {...props} />,
  h4: (props: TextProps) => <Text className="text-left" selectable variant="h4" {...props} />,
  HanziScriptTable: SlideHanziScriptTableSlot,
  HanziScriptToggle: SlideHanziScriptToggle,
  li: (props: TextProps) => (
    <Text className="mt-0 text-left leading-[1.35] sm:mt-0" selectable variant="p" {...props} />
  ),
  p: (props: TextProps) => (
    <Text className="mt-0 text-left leading-[1.35] sm:mt-0" selectable variant="p" {...props} />
  ),
  SignInButton,
  SlideFooter,
  SlideGlyph,
  // Wraps every top-level block (the Metro transformer inserts it) so the card can page the slide.
  SlidePageBlock,
  SlideToneSwatch,
  SvgGlyph: SlideSvgGlyph,
  // Inline text run — author `<Span className="text-tone-4">…</Span>` to color a
  // substring. Routes through the project `Text`, which merges the surrounding
  // `TextClassContext` (e.g. a table cell's text class) before its own `className`
  // — so inside a `TD`/`TH` the span inherits the cell's exact size/metrics and
  // only overrides color, keeping the colored run aligned with its sibling text.
  span: (props: TextProps) => <Text {...props} />,
  Span: (props: TextProps) => <Text {...props} />,
  strong: (props: TextProps) => <Text className="font-bold" {...props} />,
  // The themed table grid. A GFM pipe table (`remark-gfm` runs in the slide compiler) emits
  // markdown `table`/`tr`/`td` elements, which arrive under the lowercase keys. As JSX in slide
  // `.mdx`, use the **uppercase** names — a lowercase `<td>` JSX tag compiles to a raw intrinsic
  // element (`_jsx("td", …)`) that bypasses this map and crashes RN with "unsupported React DOM
  // element".
  caption: Caption,
  Caption,
  SlideOptions,
  SlideTitle,
  table: Table,
  Table,
  tbody: TBody,
  TBody,
  td: TD,
  TD,
  TextSizeSlider: SlideTextSizeSlider,
  tfoot: TFoot,
  TFoot,
  th: TH,
  TH,
  thead: THead,
  THead,
  ToneColorsToggle: SlideToneColorsToggle,
  tr: TR,
  TR,
  wrapper: SlideMdxWrapper,
};
